<template>
  <div class="flex justify-center items-center w-full h-screen">
    <div class="w-3/5 flex h-screen bg-blue justify-center items-center">
      <div class="w-full h-full overflow-hidden bg-blue-800 pt-12 pl-24 text-white" :style="{
        background: `url(${loginPattern1}) no-repeat top right, url(${loginPattern2}) no-repeat bottom left`,
        backgroundColor: '#03318C',
      }">
        <h1 class="text-4xl font-fig-bold font mb-0 pt-12 leading-none inline-block" style="color: #FFC72D">
          Doctor Dashboard
        </h1>
        <p class="text-2xl font-fig-semibold mb-0 mt-4 mr-32 break-words leading-normal">
          Empowering doctors to deliver healthcare
        </p>
        <p class="text-2xl font-fig-semibold mb-0 mr-32 break-words leading-normal">
          through seamless virtual consultations.
        </p>
        <div class="mt-12 w-9/12">
          <div v-for="(item, index) in content" :key="index" class="flex items-center mt-8">
            <img :src="item.image" class="mr-5" />
            <div>
              <div class="text-lg font-fig-normal mb-0 break-words leading-normal">
                {{ item.heading }}
              </div>
              <span class="text-lg font-fig-normal mb-0 break-words leading-normal">
                {{ item.text }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex justify-between mt-12 mr-80 ">
          <div v-for="(item, index) in keyPoints" :key="index" class="items-center mt-8 mb-0">
            <div class="text-4xl font-fig-bold mb-0 break-words">
              {{ item.heading }}
            </div>
            <div class="text-sm mt-2 font-fig-normal mb-0 break-words">
              {{ item.text }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isEmailVerified" class="w-2/5 flex flex-col h-screen justify-center items-center">
      <div class="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <label class="block text-grey-darker text-base font-semibold mb-2">
          We just emailed you a link
        </label>
        <p class="text-base pt-1">Please check your email and click the secure link.</p>
        <p class="text-base pt-2">If you don’t see our email, check your spam folder</p>
      </div>
    </div>
    <div v-else class="w-2/5 flex flex-col h-screen justify-center items-center">
      <form v-if="isForgotPassword" class="w-2/3 bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
        <div class="mb-6">
          <label class="block text-grey-darker text-base font-semibold mb-2">
            What's your email address?
          </label>
          <input
            class="shadow appearance-none border rounded w-full py-2 px-3 mb-3 text-grey-darker leading-tight focus:outline-none focus:shadow-outline"
            id="email" type="email" placeholder="Email" v-model="email" value="" maxlength="50" :has-counter="false">
          <p v-if='error && email' class="text-red text-xs italic">{{ emailError }}</p>
        </div>
        <div class="flex items-center justify-between">
          <button @click="goBack"
            class="bg-blue hover:bg-blue-dark text-white font-semibold py-2 px-4 rounded focus:outline-none focus:shadow-outline">
            Back
          </button>
          <button @click="submitEmail" type="submit"
            class="bg-blue self-center hover:bg-blue-dark text-white font-semibold py-2 px-4 rounded focus:outline-none focus:shadow-outline">
            Next
            <i v-if="isLoading" class="ml-2 fa fa-spinner fa-spin"></i>
          </button>
        </div>
      </form>
      <div v-else-if="secureAccount" class="w-2/5 flex flex-col h-screen justify-center items-center">
        <div class="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <div class="mb-6">
            <label class="block text-grey-darker text-base font-semibold mb-2">
              Secure your account
            </label>
            <p class="text-sm mb-2 text-grey-dark pt-1">You are required to enroll in Multi-Factor Authentication (MFA)
              for a extra layer of protection for your account.</p>
            <p class="text-sm pt-2 text-grey-dark">Enroll using Google Authenticator or Authy app.</p>
          </div>
          <div class="flex items-center justify-between mt-4">
            <button type="submit" @click="enableMfaSetting"
              class="bg-blue hover:bg-blue-dark text-white font-semibold py-2 px-4 rounded focus:outline-none focus:shadow-outline">
              Continue
            </button>
            <div @click="skipMfaSetting"
              class="cursor-pointer text-sm italic text-grey-dark hover:text-blue hover:font-semibold">
              <a href="#">Skip</a>
            </div>
          </div>
        </div>
      </div>
      <form v-else v-on:submit="submit()">
        <div class="mt-12 bg-white">
          <img :src="curaLogo" />
          <h1 class="mb-0 text-gray-900 mt-10 text-2xl font-fig-bold leading-none">
            Log in to Doctor Dashboard
          </h1>
          <div class="mt-12 text-gray-600 text-xs flex flex-col w-full">
            <span class="mb-3 text-gray-700 text-sm font-fig-semibold">Email address</span>
            <input id="input_username" type="text" name="username" placeholder="enter email" v-model="credentials.username"
               :class="[
                'text-base caret-blue-600 text-gray-700 leading-4 h-12 placeholder-gray-300 font-fig-semibold pl-4 border border-gray-200 rounded-md w-96 focus:border-gray-400 bg-gray-100 focus:bg-gray-100 focus:ring-0 focus:outline-none',
                usernameError ? 'border-red-600' : 'focus:border-gray-400',
              ]" />
            <p v-if="error && usernameError" class="mb-0 mt-2 text-xs font-fig-semibold text-red-600 flex flex-row">
              <img :src="alertIcon" class="h-3 w-3 mr-1" />
              {{ usernameError }}
            </p>
            <span class="mt-8 mb-3 text-gray-700 text-sm font-fig-semibold">Password</span>
            <div class="flex items-end">
              <input id="input_password" :type="showPassword ? 'text' : 'password'" name="password" placeholder="*********" v-model="credentials.password"
                 :class="[
                  'text-base caret-blue-600 text-gray-700 placeholder-gray-300 leading-4 font-fig-semibold pl-4 border border-gray-200 focus:border-gray-400 rounded-md w-96 h-12 bg-gray-100 focus:bg-gray-100 focus:ring-0 focus:outline-none',
                  passwordError ? 'border-red-600' : 'focus:border-gray-400',
                ]" />
              <img :src="showPassword ? eye : eyeoff"
                class="inline-block h-6 w-6 relative bottom-3 right-10 cursor-pointer" @click="toggleVisibility"
                alt="eye_icon" />
            </div>
            <p v-if='error && passwordError' class="mb-0 mt-2 font-fig-semibold text-xs text-red-600 flex flex-row">
              <img :src="alertIcon" class="h-3 w-3 mr-1" />
              <span class="break-words">{{ passwordError }}</span>
            </p>
            <button type="submit" :disabled="disabled || isLoading" :class="[
              'text-white w-96 py-4 rounded-md text-lg mt-16 font-fig-semibold focus:outline-none',
              (!credentials.password || !credentials.username || isLoading)
                ? 'bg-gray-300 hover:bg-slate-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 cursor-pointer',
            ]">
              Log in
            </button>
            <div class="flex justify-end mt-4 mr-6" @click="forgotPassword">
               <a class="mb-6 text-blue-500 font-fig-normal" href="#">Forgot password?</a>
            </div> 
              <p class="mb-0 font-fig-normal text-gray-600 mt-8 text-sm w-96 text-center">
                By logging in, you will agree with ekincare’s
              <a href="https://www.ekincare.com/about/terms" target="_blank" rel="noopener noreferrer"
                class="text-blue-500 cursor-pointer hover:text-blue-500 hover:underline">
                Terms and conditions
              </a>
              and
              <a href="https://www.ekincare.com/about/privacy" target="_blank" rel="noopener noreferrer"
                class="text-blue-500 cursor-pointer hover:text-blue-500 hover:underline">
                privacy policy
              </a>
            </p>
            <div class="text-gray-500 mt-12 text-center">
              A product by
              <a href="https://www.ekincare.com" target="_blank" rel="noopener noreferrer"
                class="text-gray-700 text-xs font-fig-semibold cursor-pointer hover:text-blue-900 hover:underline">
                ekincare
              </a>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import router from '../router/index.js'
import Raven from 'raven-js'
import { mapState } from 'vuex'
import VueToast from 'vue-toast-notification'
import 'vue-toast-notification/dist/index.css'
import Vue from 'vue'
import localforage from 'localforage'
import { content, keyPoints } from '../data/loginContent'
import loginPattern1 from '../assets/login-pattern1.svg'
import loginPattern2 from '../assets/login-pattern2.svg'
import curaLogo from '../assets/cura-hub.svg'
import alertIcon from '../assets/alert_icon.svg'
import eye from '../assets/eye.svg'
import eyeoff from '../assets/eyeoff.svg'

Vue.use(VueToast)
export default {
  data () {
    return {
      credentials: {
        username: '',
        password: '',
        url: 'online_doctors/sign_in'
      },
      error: false,
      showAlreadyLogged: false,
      showLogin: true,
      isLoading: false,
      isActive: true,
      isClosable: false,
      isFullPage: true,
      usernameError: null,
      passwordError: null,
      emailError: null,
      isForgotPassword: false,
      email: null,
      isEmailVerified: false,
      secureAccount: false,
      content,
      keyPoints,
      loginPattern1,
      loginPattern2,
      disabled: false,
      showPassword: false,
      curaLogo,
      alertIcon,
      eye,
      eyeoff
    }
  },
  computed: {
    ...mapState({
      role: state => state.configuration.role
    })
  },
  methods: {
    submit () {
      event.preventDefault()
      if (this.credentials.username.length < 2) {
        this.usernameError = 'Please enter username'
        this.passwordError = null
        this.error = true
        return
      }
      if (this.credentials.password.length < 1) {
        this.passwordError = 'Please enter a password'
        this.usernameError = null
        this.error = true
        return
      }
      var credentials = {
        email: this.credentials.username,
        password: this.credentials.password
      }
      var mfaToken = sessionStorage.getItem('mfa_token')
      if (mfaToken) {
        credentials = { mfa_token: mfaToken }
      }
      this.isLoading = true
      this.$http.post(window.API_URL + this.credentials.url, credentials).then(response => {
        if (response.status === 200) {
          if (response.headers.get('X-DOCTOR-KEY')) {
            Raven.setUserContext({ name: response.body.doctor.name, id: response.body.doctor.doctor_id })
            sessionStorage.setItem('X-DOCTOR-KEY', response.headers.get('X-DOCTOR-KEY'))
            sessionStorage.setItem('availableForChat', false)
            sessionStorage.setItem('isLoggedIn', true)
            sessionStorage.removeItem('mfa_token')
            this.$store.dispatch('configuration/init')
            if (response.body.doctor.role === 'admin') {
              sessionStorage.setItem('isAdmin', true)
              router.push('admin-home')
            } else {
              router.push('home')
            }
            this.clearLocalForage()
          } else if (response.body.mfa_token) {
            sessionStorage.setItem('mfa_token', response.body.mfa_token)
            sessionStorage.setItem('role', response.body.role)
            this.secureAccount = true
          } else if (response.body.mfa_enabled) {
            sessionStorage.setItem('mfa_enabled', response.body.mfa_enabled)
            sessionStorage.setItem('mfa_type', response.body.mfa_type)
            router.push({
              name: 'MFA-OTP',
              params: {
                username: this.credentials.username,
                password: this.credentials.password,
                type: response.body.mfa_type
              }
            })
          } else {
            this.isLoading = false
            this.error = true
            if (response.body.message === 'Invalid Credentials') {
              this.passwordError = 'Password does not match. Please enter correct password to continue.'
              this.usernameError = null
            } else if (response.body.message === 'account locked') {
              this.$toast.open({
                message: 'Your account has been locked, please try after 24 hours.',
                type: 'error',
                position: 'top',
                queue: false
              })
            } else {
              this.usernameError = 'Email does not exist. Please enter correct email to continue.'
              this.passwordError = null
            }
          }
        }
      }, response => {
      })
    },
    forgotPassword: function () {
      this.isForgotPassword = true
    },
    submitEmail: function () {
      event.preventDefault()
      var email = this.email
      this.isLoading = true
      this.$http.post(window.API_URL + 'doctors/verify_email?email=' + email).then(response => {
        if (response.status === 200) {
          this.isLoading = false
          console.log('mail has been sent to your email')
          this.isEmailVerified = true
          this.error = false
          this.emailError = null
        }
      }).catch(function (error) {
        this.isLoading = false
        console.log(error)
        if (error.status === 401) {
          this.isEmailVerified = false
          this.error = true
          this.emailError = error.body.message
        } else if (error.status === 500) {
          this.$toast.open({
            message: error.body.message,
            type: 'error',
            position: 'top',
            queue: false
          })
        }
      })
    },
    goBack: function () {
      this.isForgotPassword = false
      this.isEmailVerified = false
      this.email = null
      this.error = false
      this.emailError = null
    },
    enableMfaSetting: function () {
      this.secureAccount = false
      router.push('mfa-settings')
    },
    skipMfaSetting: function () {
      this.credentials.url = 'doctors/mfa_settings/skip_enable_mfa'
      this.submit()
    },

    toggleVisibility () {
      this.showPassword = !this.showPassword
    },

    clearLocalForage () {
      localforage.iterate((data, key, index) => {
        if (index === 1) {
          return [key, data]
        }
      }).then((result) => {
        let data = result[1]
        let i = data.length
        while (i--) {
          let entryDate = new Date(data[i].created_at)
          let now = new Date()
          let diff = Math.abs(now - entryDate)
          let diffDays = Math.ceil(diff / (1000 * 60 * 60 * 24))
          if (diffDays >= 3) {
            data.splice(i, 1)
          }
        }
        localforage.setItem('audits', data)
      })
    }
  },
  mounted: function () {
    if (!sessionStorage.length) {
      // Ask other tabs for session storage
      localStorage.setItem('getSessionStorage', Date.now())
    }
    if (sessionStorage.getItem('X-DOCTOR-KEY') !== null && sessionStorage.getItem('X-DOCTOR-KEY') !== '') {
      this.showAlreadyLogged = true
      this.showLogin = false
    } else {
      window.addEventListener('storage', function (event) {
        if (event.key === 'getSessionStorage') {
          // Some tab asked for the sessionStorage -> send it
          localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage))
          localStorage.removeItem('sessionStorage')
        } else if (event.key === 'sessionStorage' && !sessionStorage.length) {
          // sessionStorage is empty -> fill it
          var data = JSON.parse(event.newValue)
          for (let key in data) { sessionStorage.setItem(key, data[key]) }
          if (sessionStorage.getItem('X-DOCTOR-KEY') !== null && sessionStorage.getItem('X-DOCTOR-KEY') !== '') {
            this.showAlreadyLogged = true
            this.showLogin = false
          } else {
            this.showAlreadyLogged = false
            this.showLogin = true
          }
        }
      })
    }
  }
}

</script>

<style lang="scss" scoped>
$danger: hsl(348, 100%, 61%);

.error-text {
  color: $danger;
  margin-bottom: 10px;
}

.title {
  text-align: center;
}

.relative {
  position: relative;
}

.caret-blue-600 {
  caret-color: #0951e1;
}

.text-gray-700 {
  --tw-text-opacity: 1;
  color: rgba(50, 68, 103, var(--tw-text-opacity));
}

.leading-4 {
  line-height: 1rem;
}

.placeholder-gray-300:-ms-input-placeholder {
  --tw-placeholder-opacity: 1;
  color: rgba(164, 176, 204, var(--tw-placeholder-opacity));
}

.placeholder-gray-300::placeholder {
  --tw-placeholder-opacity: 1;
  color: rgba(164, 176, 204, var(--tw-placeholder-opacity));
}

.border-gray-200 {
  --tw-border-opacity: 1;
  border-color: rgba(229, 232, 240, var(--tw-border-opacity));
}

.rounded-md {
  border-radius: 0.375rem;
}

.border-gray-400 {
  --tw-border-opacity: 1;
  border-color: rgba(136, 149, 175, var(--tw-border-opacity));
}

.bg-gray-100 {
  --tw-bg-opacity: 1;
  background-color: rgba(245, 247, 249, var(--tw-bg-opacity));
}

.text-gray-600 {
  --tw-text-opacity: 1;
  color: rgba(77, 97, 137, var(--tw-text-opacity));
}

.border-red-600 {
  --tw-border-opacity: 1;
  border-color: rgba(215, 70, 81, var(--tw-border-opacity));
}

.text-red-600 {
  --tw-text-opacity: 1;
  color: rgba(215, 70, 81, var(--tw-text-opacity));
}

.bg-gray-300 {
  --tw-bg-opacity: 1;
  background-color: rgba(164, 176, 204, var(--tw-bg-opacity));
}

.bg-blue-600 {
  --tw-bg-opacity: 1;
  background-color: rgba(9, 81, 225, var(--tw-bg-opacity));
}

.text-blue-500 {
  --tw-text-opacity: 1;
  color: rgba(33, 104, 243, var(--tw-text-opacity));
}

.bottom-3 {
  bottom: 0.75rem;
}

.right-10 {
  right: 2.5rem;
}

.cursor-pointer {
  cursor: pointer;
}

.mr-80 {
  margin-right: 20rem;
}

.font-fig-normal {
  font-family: 'Figtree-Medium';
}

.font-fig-semibold {
  font-family: 'Figtree-SemiBold';
}

.font-fig-bold {
  font-family: 'Figtree-Bold';
}

.font-fig-extrabold {
  font-family: 'Figtree-ExtraBold';
} 
</style>



// WEBPACK FOOTER //
// src/components/Login.vue